import { styled } from "@linaria/react"
import { AnimatePresence, motion } from "framer-motion"
import { observer } from "mobx-react-lite"

import { Button } from "@/components/Button/Button.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { FormField } from "@/components/FormField/FormField.tsx"
import { Heading2 } from "@/components/Heading/Heading.tsx"
import { FullWidthInput } from "@/components/Input/Input.tsx"
import { CheckmarkMedium, Icon16 } from "@/icons/Icons.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { ProjectGeneralTabModel } from "@/components/ProjectSettingsDialog/ProjectGeneralTabModel.ts"

export const ProjectNameEditor = observer(({ model, canEditProject }: {
  model: ProjectGeneralTabModel
  canEditProject: boolean
}) => {
  const name = model.name.get()
  const isSaving = model.isSaving.get()
  const canSave = model.canSave.get()

  return (
    <Container>
      <Heading2>Project Name</Heading2>

      <ProjectNameField>
        <FullWidthInput
          value={name}
          onChange={(e) => model.setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              void model.handleSave()
            } else if (e.key === "Escape") {
              e.preventDefault()
              model.handleCancel()
            }
          }}
          placeholder="Enter project name"
          disabled={!canEditProject || isSaving}
        />

        <AnimatePresence mode="wait">
          {canSave && (
            <motion.div
              key="save-button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
            >
              <SaveButton size="small" kind="alternative" onClick={model.handleSave} disabled={isSaving}>
                {isSaving ? ellipsize("Saving") : (
                  <Horizontal gap="4px" alignItems="center">
                    <CheckmarkMedium className={Icon16} color="var(--success-color)" />
                    Save
                  </Horizontal>
                )}
              </SaveButton>
            </motion.div>
          )}
        </AnimatePresence>
      </ProjectNameField>
    </Container>
  )
})

const Container = styled(Vertical)`
  gap: 16px;

  margin-bottom: 16px;
`

const ProjectNameField = styled(FormField)`
  position: relative;
`

const SaveButton = styled(Button)`
  position: absolute;
  bottom: 8px;
  right: 8px;
`
